import type {
  TransferProviderCommunicationTimeliness,
  TransferProviderCurrentEngagement,
  TransferProviderRelationshipStrength,
} from "@/services/api/types";
import { statusColours } from "@/styles/statusColours";

export type TransferProviderStatus = {
  label: string;
  description: string;
  colour: string;
  backgroundColour: string;
};

export const getTransferProviderStatus = (
  lookup: Readonly<Record<string, TransferProviderStatus>>,
  value: string,
): TransferProviderStatus =>
  lookup[value] ?? {
    label: value.replace(/_/g, " ").replace(/^./, (character: string) =>
      character.toUpperCase(),
    ),
    description: "Status details are not yet available",
    colour: "#A4A2B0",
    backgroundColour: "#F7F6F8",
  };

export const relationshipStrengthLookup: Record<
  TransferProviderRelationshipStrength,
  TransferProviderStatus
> = {
  strategic: {
    label: "Strategic",
    description: "Highly collaborative, proactive and strong partnership",
    ...statusColours.green,
  },
  collaborative: {
    label: "Collaborative",
    description: "Engaged, responsive, easy to work with",
    ...statusColours.yellow,
  },
  moderate: {
    label: "Moderate",
    description: "Cooperative but mostly transactional",
    ...statusColours.orange,
  },
  resistant: {
    label: "Resistant",
    description: "Limited or no engagement, difficult to progress",
    ...statusColours.red,
  },
};

export const currentEngagementLookup: Record<
  TransferProviderCurrentEngagement,
  TransferProviderStatus
> = {
  regular: {
    label: "Regular calls",
    description: "Regular call schedule currently in place",
    ...statusColours.green,
  },
  adhoc: {
    label: "Ad-hoc calls",
    description: "Will communicate via calls when requested",
    ...statusColours.yellow,
  },
  email: {
    label: "Email",
    description: "Responsive via email",
    ...statusColours.orange,
  },
  not_responsive: {
    label: "Not responsive",
    description: "Has not responded to email(s)",
    ...statusColours.red,
  },
};

export const communicationTimelinessLookup: Record<
  TransferProviderCommunicationTimeliness,
  TransferProviderStatus
> = {
  exceptional: {
    label: "Exceptional",
    description: "Responds quickly within 1-2 business days",
    ...statusColours.green,
  },
  strong: {
    label: "Strong",
    description: "Responds fairly quickly within 3-5 business days",
    ...statusColours.yellow,
  },
  standard: {
    label: "Standard",
    description: "Responds within 5-10 business days",
    ...statusColours.orange,
  },
  delayed: {
    label: "Delayed",
    description: "Responds within 10+ business days",
    ...statusColours.red,
  },
};